import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  createFilterOptions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import useSlideSearch from 'components/SearchFilters/MainFilters/FullSearch/useSlideSearch';
import { SlideSearchData } from 'interfaces/searchIndex';
import { Slide } from 'interfaces/slide';
import { compact, every, filter, find, map, some, take, uniq, uniqBy } from 'lodash';
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { splitSearchTerms } from 'services/matchSort';

import { useCrossLabSlideSearches } from './useCrossLabSlideSearches';

interface SlideDirectAddSearchProps {
  availablePendingSlides: Slide[];
  canSearchAcrossLabs: boolean;
  onSelectedSlidesChange: React.Dispatch<React.SetStateAction<Slide[]>>;
  selectedSlides: Slide[];
  selectionResetKey: number;
  targetStudyId: string;
}

const filterOptions = createFilterOptions<Slide>({
  stringify: (option) => `${option.originalFileName || ''} ${option.id}`,
});

const listSeparatorPattern = /[,\n\r\t]/;

const toSlide = (result: SlideSearchData): Slide => ({
  id: result.id,
  originalFileName: result.fileName,
  labId: result.labId,
  sourceLabName: result.labName,
  sourceStudyId: result.studyId,
});

const getUnmatchedMessage = (unmatchedSlideIds: string[]) => {
  const visibleSlideIds = take(unmatchedSlideIds, 5);
  const omittedCount = unmatchedSlideIds.length - visibleSlideIds.length;
  return `No accessible slide matched ${visibleSlideIds.join(', ')}${
    omittedCount > 0 ? ` and ${omittedCount} more` : ''
  }.`;
};

interface CrossLabSlideSearchProps {
  onSelectedSlidesChange: React.Dispatch<React.SetStateAction<Slide[]>>;
  selectedSlides: Slide[];
  selectionResetKey: number;
  targetStudyId: string;
}

const CrossLabSlideSearch: FunctionComponent<CrossLabSlideSearchProps> = ({
  onSelectedSlidesChange,
  selectedSlides,
  selectionResetKey,
  targetStudyId,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const resolvedBatchKeyRef = useRef('');
  const selectionResetKeyRef = useRef(selectionResetKey);
  const isListSearch = listSeparatorPattern.test(searchInput);
  const slideIds = useMemo(
    () => (isListSearch ? uniq(compact(map(splitSearchTerms(searchInput), (slideId) => slideId.trim()))) : []),
    [isListSearch, searchInput]
  );
  const singleSearchQuery = useSlideSearch(searchInput, !isListSearch);
  const slideIdSearchQueries = useCrossLabSlideSearches(slideIds, isListSearch);
  const listSearchSettled =
    isListSearch && every(slideIdSearchQueries, (query) => !query.isFetching && !query.isLoading);
  const listMatches = useMemo(
    () =>
      compact(
        map(slideIds, (slideId, index) => {
          const exactMatch = find(slideIdSearchQueries[index]?.data?.results || [], { id: slideId });
          return exactMatch ? toSlide(exactMatch) : undefined;
        })
      ),
    [slideIdSearchQueries, slideIds]
  );
  const unmatchedSlideIds = listSearchSettled ? filter(slideIds, (slideId) => !some(listMatches, { id: slideId })) : [];
  const singleSearchMatches = uniqBy(map(singleSearchQuery.data?.results || [], toSlide), 'id');
  const displayedMatches = isListSearch ? listMatches : singleSearchMatches;
  const isLoading = isListSearch
    ? some(slideIdSearchQueries, (query) => query.isFetching || query.isLoading)
    : singleSearchQuery.isFetching;
  const hasSearchError = isListSearch ? some(slideIdSearchQueries, 'isError') : singleSearchQuery.isError;

  useEffect(() => {
    if (selectionResetKeyRef.current === selectionResetKey) return;
    selectionResetKeyRef.current = selectionResetKey;
    setSearchInput('');
  }, [selectionResetKey]);

  useEffect(() => {
    const batchKey = slideIds.join('\n');
    // The batch guard stays until the cleared input is reflected here, so the settled previous results are not reselected.
    if (!batchKey) {
      resolvedBatchKeyRef.current = '';
      return;
    }
    if (!listSearchSettled || batchKey === resolvedBatchKeyRef.current) {
      return;
    }

    resolvedBatchKeyRef.current = batchKey;
    onSelectedSlidesChange((currentSlides) => uniqBy([...currentSlides, ...listMatches], 'id'));
  }, [listMatches, listSearchSettled, onSelectedSlidesChange, slideIds]);

  const toggleSlide = (slide: Slide) => {
    onSelectedSlidesChange((currentSlides) =>
      some(currentSlides, { id: slide.id })
        ? filter(currentSlides, (currentSlide) => currentSlide.id !== slide.id)
        : [...currentSlides, slide]
    );
  };

  return (
    <Stack spacing={1.5}>
      <TextField
        label="Find slides from other labs"
        value={searchInput}
        onChange={(event) => setSearchInput(event.target.value)}
        multiline
        minRows={2}
        helperText={`Search across labs and add slides to study ${targetStudyId}. Paste slide IDs separated by commas, new lines, or tabs.`}
        FormHelperTextProps={{ sx: { typography: 'body1', fontSize: 16 } }}
      />
      {isLoading ? (
        <Stack direction="row" spacing={1} alignItems="center" role="status">
          <CircularProgress size={20} />
          <Typography variant="body2">Searching for slides...</Typography>
        </Stack>
      ) : null}
      {hasSearchError ? <Alert severity="error">Slide search could not be loaded.</Alert> : null}
      {unmatchedSlideIds.length > 0 ? <Alert severity="warning">{getUnmatchedMessage(unmatchedSlideIds)}</Alert> : null}
      {searchInput && !isListSearch && !isLoading && !hasSearchError && displayedMatches.length === 0 ? (
        <Alert severity="info">No matching slides found.</Alert>
      ) : null}
      {displayedMatches.length > 0 ? (
        <Box sx={{ border: 1, borderColor: 'divider', maxHeight: 280, overflowY: 'auto' }}>
          <List disablePadding aria-label="Matching slides">
            {map(displayedMatches, (slide) => {
              const selected = some(selectedSlides, { id: slide.id });
              return (
                <ListItem key={slide.id} disablePadding>
                  <ListItemButton onClick={() => toggleSlide(slide)} selected={selected}>
                    <Checkbox
                      edge="start"
                      checked={selected}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ 'aria-label': `Select ${slide.originalFileName || slide.id}` }}
                    />
                    <ListItemText
                      primary={slide.originalFileName || slide.id}
                      secondary={[slide.sourceLabName || slide.labId, slide.sourceStudyId || slide.id]
                        .filter(Boolean)
                        .join(' / ')}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      ) : null}
    </Stack>
  );
};

interface PendingSlideSearchProps {
  availablePendingSlides: Slide[];
  onSelectedSlidesChange: React.Dispatch<React.SetStateAction<Slide[]>>;
}

const PendingSlideSearch: FunctionComponent<PendingSlideSearchProps> = ({
  availablePendingSlides,
  onSelectedSlidesChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  return (
    <Autocomplete
      options={availablePendingSlides}
      value={null}
      inputValue={searchTerm}
      filterOptions={filterOptions}
      getOptionLabel={(option) => option.originalFileName || option.id}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onInputChange={(_event, value) => setSearchTerm(value)}
      onChange={(_event, value) => {
        if (value) {
          onSelectedSlidesChange((currentSlides) => uniqBy([...currentSlides, value], 'id'));
          setSearchTerm('');
        }
      }}
      noOptionsText="No matching slides found."
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id}>
          <ListItemText
            primary={option.originalFileName || option.id}
            secondary={[option.sourceLabName || option.labId, option.sourceStudyId || option.id]
              .filter(Boolean)
              .join(' / ')}
          />
        </ListItem>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Add a slide by name or ID"
          helperText="Search slides currently available for case assignment."
          FormHelperTextProps={{ sx: { typography: 'body1', fontSize: 16 } }}
        />
      )}
    />
  );
};

const SlideDirectAddSearch: FunctionComponent<SlideDirectAddSearchProps> = ({
  availablePendingSlides,
  canSearchAcrossLabs,
  onSelectedSlidesChange,
  selectedSlides,
  selectionResetKey,
  targetStudyId,
}) => {
  return canSearchAcrossLabs ? (
    <CrossLabSlideSearch
      onSelectedSlidesChange={onSelectedSlidesChange}
      selectedSlides={selectedSlides}
      selectionResetKey={selectionResetKey}
      targetStudyId={targetStudyId}
    />
  ) : (
    <PendingSlideSearch
      availablePendingSlides={availablePendingSlides}
      onSelectedSlidesChange={onSelectedSlidesChange}
    />
  );
};

export default SlideDirectAddSearch;
